import { doorRules } from '@planner/config';
function finite(value) {
    return Number.isFinite(value);
}
function validMillimetres(value) {
    return Number.isSafeInteger(value) && value > 0;
}
function diyWidths(measurements) {
    const widths = [measurements.widthTop, measurements.widthMiddle, measurements.widthBottom];
    return widths.every(validMillimetres) ? widths : undefined;
}
/**
 * Door we will make. DIY deducts `doorRules.clearance` from the narrowest
 * width and the shorter height. Trade is the size the customer entered.
 */
export function deriveManufacturedSize(measurements) {
    if (measurements.mode === 'trade') {
        if (!validMillimetres(measurements.width) || !validMillimetres(measurements.height)) {
            return undefined;
        }
        return { width: measurements.width, height: measurements.height };
    }
    const widths = diyWidths(measurements);
    const heights = [measurements.height, measurements.heightRight];
    if (!widths || !heights.every(validMillimetres)) {
        return undefined;
    }
    return {
        width: Math.min(...widths) - doorRules.clearance.width,
        height: Math.min(...heights) - doorRules.clearance.height,
    };
}
/** Difference between the widest and narrowest DIY width. Trade is 0. */
export function widthSpread(measurements) {
    if ('mode' in measurements && measurements.mode === 'trade') {
        return validMillimetres(measurements.width) ? 0 : undefined;
    }
    const widths = diyWidths(measurements);
    if (!widths) {
        return undefined;
    }
    return Math.max(...widths) - Math.min(...widths);
}
/**
 * DIY only: true when the width spread is at least
 * `doorRules.outOfSquareToleranceMm`. Trade is never out of square.
 */
export function isOutOfSquare(measurements) {
    if ('mode' in measurements && measurements.mode === 'trade') {
        return validMillimetres(measurements.width) && validMillimetres(measurements.height)
            ? false
            : undefined;
    }
    const spread = widthSpread(measurements);
    if (spread === undefined) {
        return undefined;
    }
    return spread >= doorRules.outOfSquareToleranceMm;
}
export function hasUnevenHeights(measurements) {
    const { height, heightRight } = measurements;
    if (!validMillimetres(height) || !validMillimetres(heightRight)) {
        return undefined;
    }
    return Math.abs(height - heightRight) > doorRules.heightSpreadToleranceMm;
}
export function classifyDepthClearance(depth) {
    if (!validMillimetres(depth)) {
        return undefined;
    }
    if (depth < doorRules.depthClearance.min) {
        return 'insufficient';
    }
    return depth < doorRules.depthClearance.recommended ? 'reduced' : 'recommended';
}
/**
 * Security triple-lock centres. Offsets come from `doorRules.securityLockOffsets`.
 * The bottom bolt is omitted when it would sit at or below 0.
 */
export function securityLockPositions(midLockHeightMm) {
    if (!finite(midLockHeightMm)) {
        return undefined;
    }
    const top = midLockHeightMm + doorRules.securityLockOffsets.top;
    const bottom = midLockHeightMm - doorRules.securityLockOffsets.bottom;
    return bottom > 0 ? { top, bottom } : { top };
}
/** Evaluate the handle and, for Security, both bolts against the manufactured door. */
export function evaluateLockFit(lockHeightMm, doorType, doorHeightMm) {
    if (!validMillimetres(lockHeightMm) ||
        !validMillimetres(doorHeightMm) ||
        (doorType !== 'barrier' && doorType !== 'security')) {
        return { status: 'invalid' };
    }
    const { min, max } = doorRules.lockHeight;
    if (lockHeightMm < min || lockHeightMm > max) {
        return { status: 'out-of-range' };
    }
    if (doorType === 'barrier') {
        return { status: 'valid' };
    }
    const locks = securityLockPositions(lockHeightMm);
    const fits = locks?.bottom !== undefined &&
        locks.bottom >= doorRules.lockEdgeClearanceMm &&
        doorHeightMm - locks.top >= doorRules.lockEdgeClearanceMm;
    return { status: fits ? 'valid' : 'clearance' };
}
/** Whether the handle and, for Security, both bolts fit the manufactured door. */
export function lockFits(lockHeightMm, doorType, doorHeightMm) {
    const { status } = evaluateLockFit(lockHeightMm, doorType, doorHeightMm);
    return status === 'invalid' ? undefined : status === 'valid';
}
/** Validate a complete door before creating a new committed line. */
export function validateDoorForCommit(door) {
    const size = deriveManufacturedSize(door.measurements);
    if (!size || !validMillimetres(size.width) || !validMillimetres(size.height)) {
        return {
            valid: false,
            message: 'Door measurements must be finite, positive safe whole millimetres',
        };
    }
    if (door.doorType !== 'barrier' && door.doorType !== 'security') {
        return { valid: false, message: 'Choose a valid door type' };
    }
    if (door.lockSide !== 'left' && door.lockSide !== 'right') {
        return { valid: false, message: 'Choose a left or right handle side' };
    }
    const fit = evaluateLockFit(door.lockHeight, door.doorType, size.height);
    switch (fit.status) {
        case 'invalid':
            return { valid: false, message: 'Handle height must be positive whole millimetres' };
        case 'out-of-range':
            return {
                valid: false,
                message: `Handle height must be between ${doorRules.lockHeight.min} and ${doorRules.lockHeight.max} mm`,
            };
        case 'clearance':
            return {
                valid: false,
                message: 'Handle position is incompatible with the manufactured door height',
            };
        case 'valid':
            return { valid: true, size };
    }
}
/** Always `doorRules.hinges.count` for a finite height. */
export function hingeCount(doorHeightMm) {
    if (!finite(doorHeightMm)) {
        return undefined;
    }
    return doorRules.hinges.count;
}
//# sourceMappingURL=derive.js.map